import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { supabaseFetch } from '../../lib/supabase';
import { useTranslation } from '../../i18n/useTranslation';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { getCountryFlag } from '../../utils/countryHelpers';
import { DEFAULT_COUNTRY_CODE, getAvailableCountries, getBaseUnitLabel } from '../../data/countriesService';
import { tokens } from '../../styles/designTokens';
import StandardButton from '../core/StandardButton';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

/**
 * MapEvents — stable inner component for Leaflet map lifecycle.
 * Defined OUTSIDE UnifiedLocationSelector so React does not recreate it
 * on every parent re-render (which would unmount/remount the useMap hook
 * and race-cancel pending fitBounds calls).
 */
const MapEvents = ({ initialBounds, initialCenter, initialZoom, currentMode, handlePinClick, mapRef, mapMountedRef, onUserPan, programmaticViewChangeRef, isViewAnimatingRef }) => {
  const map = useMap();
  mapRef.current = map;

  // Track mount state for post-unmount guards
  useEffect(() => {
    mapMountedRef.current = true;
    return () => { mapMountedRef.current = false; };
  }, [mapMountedRef]);

  // User-drag/zoom detection (2026-08-26 crosshair tuning; zoom added in the
  // 2026-08-26 save-and-exit/parity build, Part 3 P4). 'dragstart' fires
  // solely for an actual mouse/touch drag gesture, never for this
  // component's own programmatic setView/fitBounds calls (initial framing,
  // country switch, the preserved-zoom transition below) — no guard needed.
  // 'zoomstart' has no such guarantee in Leaflet 1.9.4/react-leaflet 4.2.1 —
  // it fires identically for a user's scroll/dblclick/+- zoom AND for our
  // own programmatic view changes — so it's gated on
  // programmaticViewChangeRef, which every one of our own view-changing
  // calls sets true immediately before acting and clears a frame later.
  useEffect(() => {
    if (!onUserPan) return;
    const handleZoomStart = () => {
      if (programmaticViewChangeRef?.current) return;
      onUserPan();
    };
    map.on('dragstart', onUserPan);
    map.on('zoomstart', handleZoomStart);
    return () => {
      map.off('dragstart', onUserPan);
      map.off('zoomstart', handleZoomStart);
    };
  }, [map, onUserPan, programmaticViewChangeRef]);

  // Motion tracking for handleSetTargetLocation's settle guard (2026-09-01
  // crosshair regression fix; widened same day from zoom-only to also cover
  // pan inertia). Deliberately its own effect, not folded into the
  // onUserPan one above — that one is wired to null outside home-building/
  // picker mode (see the prop below), but the click-to-set-target flow this
  // guards runs in every mode. 'movestart'/'moveend' rather than
  // 'zoomstart'/'zoomend': Leaflet fires 'movestart' for a zoom animation
  // too, and an inertia glide after a flick-drag continues the same move
  // (Leaflet suppresses a second 'movestart' for it) through to one
  // 'moveend' when the glide fully stops — so this single pair is the
  // settle signal for both gestures, not just zoom.
  useEffect(() => {
    if (!isViewAnimatingRef) return;
    const handleMoveStart = () => {
      if (programmaticViewChangeRef?.current) return;
      isViewAnimatingRef.current = true;
    };
    const handleMoveEnd = () => {
      isViewAnimatingRef.current = false;
    };
    map.on('movestart', handleMoveStart);
    map.on('moveend', handleMoveEnd);
    return () => {
      map.off('movestart', handleMoveStart);
      map.off('moveend', handleMoveEnd);
    };
  }, [map, isViewAnimatingRef, programmaticViewChangeRef]);

  // Apply the initial view once the map is ready and has container size.
  // Uses whenReady + invalidateSize to guarantee the container is laid out.
  // Guarded (2026-08-26 panBy crash fix) against firing after the component/map
  // has been torn down (mapMountedRef — declared for this purpose but not
  // previously read here) and against a container that is still genuinely
  // 0x0 at the moment the deferred callback runs (re-checked at fire time, not
  // just at scheduling time), with a bounded retry rather than an unconditioned
  // fitBounds against a degenerate viewport. Leaflet's own `whenReady` callback
  // can't be unregistered, so the guard has to live inside doFit itself.
  // When initialCenter/initialZoom are both given (2026-08-26 save-and-exit/
  // parity build, Part 3 P3), setView to that exact view instead of
  // fitBounds(initialBounds) — used to carry the zoom the user had chosen on
  // a prior stage forward, rather than reframing via a fresh bounding box.
  useEffect(() => {
    const hasPreservedView = initialCenter != null && initialZoom != null;
    if (!hasPreservedView && !initialBounds) return;
    if (!map) return;
    let cancelled = false;
    let rafId = null;
    let retries = 0;
    const MAX_RETRIES = 10;

    const doFit = () => {
      if (cancelled || !mapMountedRef.current) return;
      const size = map.getSize();
      if ((size.x === 0 || size.y === 0) && retries < MAX_RETRIES) {
        retries += 1;
        rafId = requestAnimationFrame(doFit);
        return;
      }
      if (programmaticViewChangeRef) programmaticViewChangeRef.current = true;
      map.invalidateSize({ animate: false });
      if (hasPreservedView) {
        map.setView(initialCenter, initialZoom, { animate: false });
      } else {
        map.fitBounds(initialBounds, {
          padding: [60, 60],
          animate: false,
          duration: 0
        });
      }
      if (programmaticViewChangeRef) {
        requestAnimationFrame(() => { programmaticViewChangeRef.current = false; });
      }
    };
    // If the map is already ready (container sized), fit now via rAF;
    // otherwise wait for the whenReady event.
    if (map.getSize().x > 0 && map.getSize().y > 0) {
      rafId = requestAnimationFrame(doFit);
    } else {
      map.whenReady(doFit);
    }

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
    };
  // initialCenter must be referentially stable across the caller's renders: a fresh array each
  // render re-frames the map every time (the #88 addendum 3 re-zoom-on-select bug).
  }, [initialBounds, initialCenter, initialZoom, map, mapMountedRef, programmaticViewChangeRef]);

  // Pin-drop mode click handler
  useEffect(() => {
    if (currentMode === 'pin') {
      map.on('click', handlePinClick);
      return () => { map.off('click', handlePinClick); };
    }
  }, [map, currentMode, handlePinClick]);

  return null;
};

/**
 * wouldStrandSelection — contiguity guard (2026-08-24 crosshair redesign,
 * addendum 1 ruling 2, Michael). A deselect is blocked if removing that
 * boundary would leave any other selected boundary disconnected from the
 * rest of the set. Pure frontend graph-reachability (DFS, stack-based) over
 * the already-known shared-border adjacency the crosshair engine computes for
 * grow-the-edge (see the frontier/internalAdjacency effect below) — no new
 * RPC, per the addendum's escalation boundary.
 */
const wouldStrandSelection = (boundaries, adjacency, removeId) => {
  const remainingIds = boundaries.filter(b => b.id !== removeId).map(b => b.id);
  if (remainingIds.length <= 1) return false; // nothing left to strand
  const remainingSet = new Set(remainingIds);
  const visited = new Set([remainingIds[0]]);
  const stack = [remainingIds[0]];
  while (stack.length > 0) {
    const current = stack.pop();
    const neighbours = adjacency[current] || new Set();
    neighbours.forEach(n => {
      if (remainingSet.has(n) && !visited.has(n)) {
        visited.add(n);
        stack.push(n);
      }
    });
  }
  return visited.size !== remainingIds.length;
};

/**
 * UnifiedLocationSelector Component
 *
 * Universal location selection component supporting:
 * - Boundary polygon selection (admin_boundaries) via the crosshair-at-map-
 *   centre model (2026-08-24 redesign — replaces the former level-tab/drill
 *   model): pan/zoom the map freely, click "Set the target location" to
 *   resolve the crosshair's point to a boundary, then grow the área by
 *   selecting/deselecting among the shared-border adjacency frontier.
 * - Pin-drop mode for articles/events
 * - Multi-country support
 * - Mobile-responsive layout
 * - Single-select picker mode (multiSelect=false + onSelect) — same
 *   crosshair, but "Set the target location" is the pick itself: no grow,
 *   no multi-select.
 *
 * @param {string} mode - 'user' | 'group' | 'article' | 'event' | 'picker'
 * @param {boolean} allowBoundary - Enable boundary polygon selection
 * @param {boolean} allowPin - Enable freeform pin-drop
 * @param {boolean} multiSelect - Allow multiple boundary selections
 * @param {number[]} initialBoundaries - Array of boundary IDs for edit forms
 * @param {object} initialPin - { lat, lng } or null
 * @param {string} initialCountry - Country code (e.g., 'PT') or null
 * @param {function} onBoundaryChange - (selectedBoundaryIds: string[]) => void
 * @param {function} onPinChange - ({ lat, lng }) => void
 * @param {function} onCountryChange - (countryCode: string) => void
 * @param {function} onInitialLoad - (boundaries: object[]) => void — fires once initialBoundaries
 *   resolves via get_boundaries_by_ids. Since 2026-09-02, onCountryChange (if the loaded
 *   boundary's country differs from the current selection) is called first, same tick, before
 *   this — but callers should read country off their own onInitialLoad `data` argument rather
 *   than off state set by onCountryChange, since that state only updates on the next render.
 * @param {boolean} compact - Smaller variant for forms
 * @param {number} userLongitude - User's home longitude for globe rotation (default: -90)
 * @param {function} onSelect - Single-select callback: ({ boundaryId, name, countryCode, lat, lon }) => void
 * @param {number[]} initialBounds - [[south, west], [north, east]] for Leaflet fitBounds
 * @param {boolean} isHomeAreaBuilding - 2026-08-26 crosshair tuning: opts a multiSelect caller
 *   into the registration wizard's home-área choreography (enriched opening, staged
 *   pan-instruction/button/grow-prompt control zone, "Set my home target" wording). Every other
 *   multiSelect caller (MyLocations, AddEditGroup) is unaffected — default false preserves the
 *   plain crosshair exactly as shipped.
 * @param {number} seedBoundaryId - isHomeAreaBuilding only: a known boundary (the wizard's
 *   already-picked home) to pre-seed via get_boundaries_by_ids directly, bypassing
 *   resolve_point_to_boundary — see handleSeedByBoundaryId's header comment for why.
 * @param {function} onTargetSeeded - (boundary) => void, fired whenever a target is (re-)seeded
 *   — by button click or the enriched opening — distinct from onBoundaryChange, which also fires
 *   on ordinary grow/shrink clicks. Used by the wizard to (re-)derive the area's auto identity.
 * @param {string} copyVariant - 2026-08-26 save-and-exit/parity build: 'home' (default, unchanged
 *   wording) | 'newLocation' (MyLocations' "Create Area" — substitutes "your new location"/"your
 *   new area" for "your home"/"your home area" in the three isHomeAreaBuilding control-zone
 *   strings). No effect unless isHomeAreaBuilding is also true.
 * @param {boolean} autoFillFirstTierAdjacency - 2026-09-01 home-picker-redefine-flow-fix-build:
 *   isHomeAreaBuilding only. Every seedTarget call also auto-selects
 *   get_adjacent_freguesias(target) — core + first-tier, not just the core — instead of leaving
 *   the frontier for manual clicks. Default false preserves today's manual grow for every existing
 *   caller (MyLocations' ordinary create, the registration wizard's área stage).
 * @param {boolean} fitToInitialSelection - Addendum 3 (2026-09-01 home-picker-redefine-flow-fix-
 *   build): forces the "load initial selections" effect to fit the map to what it loaded even
 *   when the caller also supplied an explicit initialBounds/initialCenter+initialZoom — used to
 *   re-apply seedTarget's own landing fit after a remount that would otherwise discard it (a
 *   set-home session's own create-on-seed re-selects the just-inserted row, changing this
 *   component's key). 2026-09-03 map-fit-to-selection build: this is no longer what makes an
 *   ordinary initialBoundaries load fit on open — that now happens unconditionally whenever no
 *   caller-supplied explicit view exists (see hasExplicitInitialView, above the effect). Default
 *   false only matters for a caller that supplies both initialBoundaries and an explicit view and
 *   wants the fit to win anyway; no current caller does.
 * @param {string} pickerConfirmLabel - isPickerMode only: overrides the generic
 *   "Set the target location" button label for a specific single-select surface (e.g.
 *   registration's home stage — "Confirm my Home location", Part 3 P2). Other isPickerMode
 *   callers (guest Change Location, registration gate) leave this unset and keep the shared
 *   generic wording — a global rename would have changed all of them at once.
 * @param {number[]} initialCenter - [lat, lng]: paired with initialZoom to setView to an exact,
 *   already-known view instead of fitBounds(initialBounds) — Part 3 P3, carries the zoom the user
 *   had chosen on a prior stage forward rather than reframing via a fresh bounding box.
 * @param {number} initialZoom - see initialCenter.
 * @param {function} onSaveAndExit - (no args) => void. When provided, a "Save and exit"
 *   StandardButton (label overridable via saveAndExitLabel) renders once selectedBoundaries is
 *   non-empty. Introduces no new persistence of its own — every caller wires this to a commit
 *   path it already has (2026-08-26 save-and-exit/parity build, Part 2).
 * @param {string} saveAndExitLabel - overrides the default "Save and exit" wording — used by
 *   AddEditGroup, whose crosshair button does not itself persist anything (the group's own,
 *   differently-positioned footer button does) and must not read as a real save action.
 * @param {string} instructionTemplateKey - 2026-09-02 unit-label-per-country build: tLevel() key
 *   (namespace 'location') for an instruction banner rendered after the country-select block,
 *   `{unit}`-interpolated with the live selected country's base-unit endonym. Null (default)
 *   renders nothing extra — used by MapLocationPicker's non-embedded change/required modes only.
 * @param {string} instructionTemplateFallback - English fallback text for instructionTemplateKey,
 *   same tLevel(key, fallback) convention as every other fallback string in this file.
 */
const UnifiedLocationSelector = ({
  mode = 'user',
  allowBoundary = true,
  allowPin = false,
  multiSelect = true,
  maxSelections = null,
  initialBoundaries = [],
  initialPin = null,
  initialCountry = null,
  onBoundaryChange,
  onInitialLoad,
  onPinChange,
  onCountryChange,
  compact = false,
  userLongitude = -90,
  onSelect,
  initialBounds = null,
  initialCenter = null,
  initialZoom = null,
  isHomeAreaBuilding = false,
  copyVariant = 'home',
  seedBoundaryId = null,
  onTargetSeeded = null,
  // 2026-09-01 home-picker-redefine-flow-fix-build: when true (isHomeAreaBuilding callers only —
  // MyLocations gates this on its own "set-home session"), every seedTarget call also fetches and
  // auto-selects get_adjacent_freguesias(target) — core + first-tier, not just the core. Off by
  // default: ordinary area-building (an empty-slot create with no set-home session, and every
  // other isHomeAreaBuilding caller) keeps today's manual frontier-click grow unchanged.
  autoFillFirstTierAdjacency = false,
  // Addendum 3 (2026-09-01 home-picker-redefine-flow-fix-build): when true, the "load initial
  // selections" effect also fits the map to whatever it just loaded, instead of leaving the map
  // at MapContainer's own hardcoded country-default view. Exists because the set-home session's
  // own create-on-seed (handleAreaBoundaryChange, fired from inside seedTarget's
  // autoFillFirstTierAdjacency branch above) triggers a real INSERT immediately, and MyLocations
  // re-selecting the just-created row changes this component's own `key` — a genuine unmount/
  // remount, not a re-render — which throws away seedTarget's own fitBounds along with everything
  // else. MyLocations gates this on the SAME setHomeSessionAreaId used to gate unconditional
  // promotion (not a new flag), so it only ever applies to that one just-created area, once — not
  // to ordinary area-switching or any other isHomeAreaBuilding caller.
  fitToInitialSelection = false,
  // 2026-10-03 (Roadmap #88 addendum 3, A1): when true, the OPENING seed (the enriched opening's
  // seed-by-id, not a later "Set my home target" re-seed) frames the map on the target plus every
  // adjoining parish, without selecting the neighbours. Shares autoFillFirstTierAdjacency's
  // fetch, fit and fallback below. Off by default; only the account-setup área stage sets it.
  frameFirstTierAdjacency = false,
  // 2026-10-03 (#88 addendum 3, A2/A3): caller-overridable grow-prompt text (same shape as
  // saveAndExitLabel; must arrive as a t() value), and whether the country dropdown shows.
  growPromptLabel = null,
  showCountrySelect = true,
  disableInertia = false,
  pickerConfirmLabel = null,
  onSaveAndExit = null,
  saveAndExitLabel = null,
  // 2026-09-02 unit-label-per-country + selector reorder build: when set, an instruction banner
  // renders immediately after the country-select block (below), interpolating `{unit}` with the
  // currently selected country's base-unit endonym (e.g. "freguesia"/"ward"), fetched reactively
  // so a manual country switch updates the word live. instructionTemplateKey is a tLevel() key
  // (namespace 'location'); instructionTemplateFallback is its English fallback text, same
  // tLevel(key, fallback) convention used throughout this file. Both null by default — every
  // existing caller (MyLocations, AddEditGroup) is unaffected.
  instructionTemplateKey = null,
  instructionTemplateFallback = null
}) => {
  const { t } = useTranslation('location');
  const { isMobile } = useMediaQuery();

  // Picker mode: single-select with onSelect callback
  const isPickerMode = !!(onSelect && !multiSelect);

  // Cap enforcement: unlimited unless a caller passes maxSelections explicitly; picker mode = 1.
  // User-mode saved areas had a hardcoded default cap of 3 here until the Part-1a build
  // (docs/briefs/2026-08-23-part1-area-default-and-guard-retirement-build.md) retired it —
  // this was the actual enforcement point DATABASE.md's "Max 3 boundaries" rule cited.
  const effectiveMax = isPickerMode ? 1 : maxSelections;

  // State
  const [currentMode, setCurrentMode] = useState(allowBoundary ? 'boundary' : 'pin');
  const [selectedBoundaries, setSelectedBoundaries] = useState([]);
  const [pinLocation, setPinLocation] = useState(initialPin);
  const [selectedCountry, setSelectedCountry] = useState(initialCountry || DEFAULT_COUNTRY_CODE);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [capWarning, setCapWarning] = useState(false);
  const [showCountryPicker, setShowCountryPicker] = useState(false);

  // Grow-the-edge (2026-08-24 crosshair redesign). frontierBoundaries: freguesias
  // touching the current selection, not yet selected — the only boundaries ever
  // clickable-to-add (probe §E's click-gating requirement). internalAdjacency:
  // sourceId -> Set of OTHER selected ids it touches, used only for the
  // contiguity guard (wouldStrandSelection above). Both come from the same
  // per-selected-boundary get_adjacent_freguesias calls — see the effect below.
  const [frontierBoundaries, setFrontierBoundaries] = useState([]);
  const [frontierLoading, setFrontierLoading] = useState(false);
  const [internalAdjacency, setInternalAdjacency] = useState({});
  const [contiguityWarning, setContiguityWarning] = useState(false);

  // Staged control-zone choreography (2026-08-26 crosshair tuning).
  // isHomeAreaBuilding: three states — 'pan-instruction' (never oriented yet)
  // -> 'button' (oriented, no target set OR an auto-seed not yet confirmed)
  // -> 'grow-prompt' (a target is set and confirmed). homeBuilderConfirmed
  // tracks whether the CURRENT target was reached by a deliberate action
  // (button click, or a grow/shrink click) rather than sitting unconfirmed
  // from the enriched opening's auto-seed — only while unconfirmed does a
  // pan revert 'grow-prompt' back to 'button' (see handleUserPan).
  const [homeControlZone, setHomeControlZone] = useState('pan-instruction');
  const [homeBuilderConfirmed, setHomeBuilderConfirmed] = useState(false);
  // Plain picker-mode staging (any isPickerMode surface, home-building or
  // not): pan-instruction -> button, no grow phase, generic wording.
  const [pickerAwaitingPan, setPickerAwaitingPan] = useState(true);

  const mapRef = useRef(null);
  const mapMountedRef = useRef(false); // Guard against post-unmount map operations — read by MapEvents' initialBounds/doFit effect before touching the map
  // "Was this view change our own doing?" (2026-08-26 save-and-exit/parity build, Part 3 P4) —
  // set true immediately before every one of our own programmatic setView/fitBounds calls, cleared
  // a frame later. Leaflet's 'zoomstart' fires identically for a user gesture and a programmatic
  // view change (unlike 'dragstart', which only ever fires for a real drag), so this is the only
  // way to tell them apart — see MapEvents' own comment.
  const programmaticViewChangeRef = useRef(false);
  // Is the view currently in motion — a zoom animation OR a pan, including
  // inertia glide after a flick-drag? (2026-09-01 crosshair regression fix,
  // probe § A3 defect i; widened to pan inertia the same day.) Set by
  // MapEvents' own movestart/moveend listeners — 'movestart' fires for a
  // zoom animation too (Leaflet fires it alongside 'zoomstart'), and Leaflet
  // doesn't re-fire it when a drag's release rolls straight into inertia, so
  // 'moveend' alone is the single settle signal covering both gestures and
  // the glide after one of them. Gated on programmaticViewChangeRef for the
  // same reason as onUserPan above — our own setView/fitBounds calls are
  // always animate:false, but 'movestart' fires identically for those and a
  // real user gesture. handleSetTargetLocation waits this out before reading
  // getCenter()/getZoom(), so a fast zoom-or-flick-then-click can't resolve
  // a point the crosshair passed through mid-motion.
  const isViewAnimatingRef = useRef(false);
  const layersRef = useRef({});
  const initialLoadDone = useRef(false);
  const markerRef = useRef(null);
  const prevCountryRef = useRef(selectedCountry); // Track last-applied country for setView guard

  // Theme color based on mode
  const themeColor = mode === 'user' ? '#024B99' : mode === 'group' ? '#BB4D9A' : '#024B99';

  // Country map coordinates. `bounds` is still used to fit/frame the map (initial view, country
  // switch) — it is NOT passed to Leaflet as `maxBounds` (2026-08-26 map-spring-back fix). Leaflet
  // bounces the view back elastically whenever a pan pushes the VIEWPORT's edge past a maxBounds
  // rectangle — confirmed as the root cause here, not the (now-retired) viewport-resolve zoom
  // gate the original report suspected. At low zoom the whole-country viewport is wide enough
  // that almost any pan collides with the PT bounds' tight west edge (which sits right along the
  // coastline), producing exactly "dragging toward the coast snaps back"; at high zoom the
  // viewport is narrow enough that the same bounds are rarely reached, matching "works above a
  // certain zoom." The crosshair's whole interaction is free pan-to-target at any zoom, which is
  // fundamentally incompatible with a hard country clamp — removed outright (not loosened) per
  // Michael's own diagnosis (addendum 1) and the parent brief's required end-state. No
  // consequence to weigh: nothing else in this file reads `selectedCountry` off where the user
  // has panned to (boundary resolution is driven by `resolve_point_to_boundary` against whatever
  // point the crosshair sits over, and simply fails gracefully — the existing
  // `target_resolve_failed` message — if that point is outside PT/GB coverage).
  const COUNTRY_COORDS = {
    PT: {
      center: [39.5, -8.0],
      bounds: [
        [36.9, -9.5],  // Southwest
        [42.2, -6.2]   // Northeast
      ],
      zoom: 7
    },
    GB: {
      center: [54.5, -3.5],  // Center of UK
      bounds: [
        [49.5, -8.5],   // Southwest (Cornwall + margin)
        [61.2, 2.5]     // Northeast (Shetland + margin)
      ],
      zoom: 6
    }
  };

  // Get map config for selected country
  const getMapConfig = (countryCode) => {
    return COUNTRY_COORDS[countryCode] || COUNTRY_COORDS.PT;
  };

  // Translation helper with fallback support
  // Try translation first; if key not found (returns key itself), use fallback
  // 2026-09-02 unit-label-per-country build: optional third `variables` arg for interpolation
  // (e.g. { unit: 'ward' }) — when the DB has the key, t()'s own resolveTranslation already
  // interpolates it; when falling back to the JS-side `fallback` string (translation batch not
  // yet applied — the brief's own "DB-step / push ordering forgiving" requirement), this
  // interpolates the fallback itself using the identical {var}/{{var}} regex convention, so
  // callers get consistent interpolation either way.
  const tLevel = (key, fallback, variables) => {
    const fullKey = `location.${key}`;
    const translated = t(fullKey, variables);
    if (translated !== fullKey) return translated;
    if (!variables) return fallback;
    return Object.entries(variables).reduce(
      (text, [varName, value]) => text.replace(new RegExp(`(\\{\\{${varName}\\}\\}|\\{${varName}\\})`, 'g'), String(value)),
      fallback
    );
  };

  // Single color for all levels - locality blue (read from the guarded LAYOUT.md
  // token, not the mode-aware `themeColor` above — this selector's boundary
  // stroke stays locality blue regardless of mode; see docs/reuse-backlog.md
  // 2026-08-24 entry for the still-unfixed duplicate at `themeColor`)
  const LOCALITY_BLUE = tokens.colors.contextLocality;
  // Unselected-boundary stroke — a lighter, clearly-visible blue, kept
  // distinct from LOCALITY_BLUE so a selected/current boundary still reads
  // as selected against it.
  const UNSELECTED_BOUNDARY_BLUE = '#4A90D9';

  // Load available countries on mount
  useEffect(() => {
    loadAvailableCountries();
  }, []);

  // 2026-09-02 unit-label-per-country build: the current country's base-unit endonym for
  // instructionTemplateKey's {unit} interpolation — reactive on selectedCountry so a manual
  // country switch (the just-revived inline <select>) updates "freguesia"/"ward" live, not just
  // at mount. Only fetched when a caller actually wants an instruction rendered — MyLocations/
  // AddEditGroup's ordinary usage never sets instructionTemplateKey, so this never fires there.
  const [unitLabel, setUnitLabel] = useState(null);
  useEffect(() => {
    if (!instructionTemplateKey || !selectedCountry) return;
    // Advisor post-diff ADVISE: reset before the fetch, not just on resolution — otherwise a
    // PT→GB switch would keep showing "freguesia" for the fetch's duration before flipping to
    // "ward", the wrong country's endonym displayed as current. Resetting to null correctly
    // falls through to the generic fallback text while loading. getBaseUnitLabel never throws
    // (returns null on any error), so no .catch() is needed here.
    setUnitLabel(null);
    let cancelled = false;
    getBaseUnitLabel(selectedCountry).then(label => {
      if (!cancelled) setUnitLabel(label);
    });
    return () => { cancelled = true; };
  }, [instructionTemplateKey, selectedCountry]);

  // Update map view when country changes — guarded so it only fires on a
  // genuine country switch, not on initial mount (MapContainer handles that)
  // or on re-renders with the same value. No setMaxBounds here — see the
  // 2026-08-26 map-spring-back fix note above COUNTRY_COORDS for why the
  // crosshair never constrains panning at all.
  useEffect(() => {
    if (!mapRef.current || !selectedCountry) return;
    if (selectedCountry === prevCountryRef.current) return; // no real change
    prevCountryRef.current = selectedCountry;

    const config = getMapConfig(selectedCountry);
    programmaticViewChangeRef.current = true;
    mapRef.current.setView(config.center, config.zoom);
    requestAnimationFrame(() => { programmaticViewChangeRef.current = false; });
  }, [selectedCountry]);

  // Addendum 2/3 (2026-09-01 home-picker-redefine-flow-fix-build): fit the map to a set of
  // already-resolved boundaries (each carrying its own `.geojson`), not a hardcoded zoom delta.
  // Shared by the "load initial selections" effect's fitToInitialSelection branch just below
  // (re-applying a fit after the set-home session's own create-on-seed remounts this component)
  // and seedTarget's autoFillFirstTierAdjacency branch further down (the initial landing fit) —
  // one computation, two call sites, so the two fits can never silently drift apart. Reuses the
  // exact fitBounds/padding convention MapEvents' own initialBounds effect already applies
  // (:123-127, `padding: [60, 60], animate: false, duration: 0`) and the same
  // programmaticViewChangeRef guard every other imperative view change in this file wraps itself
  // in (e.g. this same country-switch setView just above), so the fit isn't mistaken for a user
  // pan. Declared here, before its first use just below, not down by seedTarget where it's also
  // used — avoids a use-before-define lint error.
  const fitToBoundaries = useCallback((boundaries) => {
    if (!mapRef.current || !boundaries?.length) return;
    const bounds = L.latLngBounds([]);
    boundaries.forEach(b => {
      if (!b.geojson) return;
      try {
        bounds.extend(L.geoJSON(b.geojson).getBounds());
      } catch (err) {
        console.error('Skipping malformed geometry in fit-to-selection:', b.id, err);
      }
    });
    if (!bounds.isValid()) return;
    programmaticViewChangeRef.current = true;
    mapRef.current.fitBounds(bounds, { padding: [60, 60], animate: false, duration: 0 });
    requestAnimationFrame(() => { programmaticViewChangeRef.current = false; });
  }, []);

  // Load initial selections (editing an existing área/boundary set) — unrelated
  // to the crosshair; the frontier effect below picks up automatically once
  // this populates selectedBoundaries.
  //
  // 2026-09-03 map-fit-to-selection build (docs/briefs/2026-09-03-map-fit-to-
  // selection-build.md): a caller that hands us initialBoundaries but ALSO an
  // explicit opening view (initialBounds, or initialCenter+initialZoom via a
  // preserveView handoff — currently only MapLocationPicker.jsx's multiSelect
  // mount, e.g. RegisterLocationStep's área stage) already knows exactly what
  // view it wants; our own fit must not override that. Every other caller of
  // initialBoundaries (MyLocations, AddEditGroup) passes none of the three, so
  // for them this is unconditional — same shape as the general rule the brief
  // asks for ("a map that opens with an area already selected frames it"),
  // computed once here rather than duplicated at each call site.
  //
  // Relies on an unenforced cross-file invariant (Advisor-flagged): the effect below reads
  // whichever value this evaluates to at the render where it first fires, then never again
  // (initialLoadDone ref, one-shot) — so if a caller ever combined initialBoundaries with an
  // opening view that resolves ASYNCHRONOUSLY after that render (openingBounds starts null in
  // MapLocationPicker.jsx, resolved later via RPC), this could read hasExplicitInitialView as
  // false and fit before the caller's real view lands. Not reachable today: the one caller doing
  // both (MapLocationPicker's multiSelect mount, via RegisterLocationStep's área stage) only ever
  // has non-empty initialBoundaries together with a synchronously-known preserveView — traced to
  // handleSetTargetLocation (below) always supplying zoom alongside lat/lon, so preserveView is
  // never "pending" when areaIds already exist. Would need re-checking if that pairing ever
  // changes.
  const hasExplicitInitialView = initialBounds != null
    || (initialCenter != null && initialZoom != null);
  const shouldFitInitialSelection = fitToInitialSelection || !hasExplicitInitialView;

  useEffect(() => {
    if (initialLoadDone.current || initialBoundaries.length === 0) return;

    const loadInitialSelections = async () => {
      try {
        const { data, error } = await supabaseFetch.rpc('get_boundaries_by_ids', {
          p_ids: initialBoundaries
        });

        if (error) throw error;

        if (data) {
          setSelectedBoundaries(data);
          // 2026-09-02 GB saved-area country sync fix (docs/briefs/2026-09-02-gb-saved-area-
          // country-sync-fix.md): a saved area's boundaries carry their own real country —
          // sync selectedCountry/onCountryChange from it here, same pairing handleCountrySelect
          // and handleCountryChange already use, instead of leaving selectedCountry on whatever
          // it was (usually the 'PT' default) until the user happens to touch the country
          // picker. This is what lets the reactive setView effect (:504-513) reframe the map on
          // the loaded boundary's actual country instead of leaving it framed on the stale one.
          const loadedCountry = data[0]?.country_code;
          if (loadedCountry && loadedCountry !== selectedCountry) {
            setSelectedCountry(loadedCountry);
            onCountryChange?.(loadedCountry);
            // Unconditional (Advisor STRONGLY ADVISE, adopted): some intentional view is about
            // to be in place either way — our own fit below when shouldFitInitialSelection is
            // true, or the caller's own explicit initialBounds/initialCenter+initialZoom when
            // it's false. Either way the reactive setView effect (:504-513) must not follow a
            // render later and stomp it with a coarse country-level default — gating this on
            // shouldFitInitialSelection alone (this build's first pass) left the
            // caller-supplied-view branch unprotected, defeating the guard on exactly the
            // combination it exists for. Same prevCountryRef guard handleCountrySelect uses at
            // :971-985 for the same reason.
            prevCountryRef.current = loadedCountry;
          }
          onInitialLoad?.(data);
          // 2026-09-03 map-fit-to-selection build: was gated on `fitToInitialSelection` alone
          // (a narrow set-home-redefine-session flag), so an ordinary open-existing-area never
          // fit — the boundaries rendered selected but the map stayed at the country default.
          // Widened to fire whenever no caller-supplied explicit view exists (see
          // hasExplicitInitialView above), which also carries forward the addendum-3 case this
          // gate originally existed for (fitToInitialSelection still forces the fit even if an
          // explicit view were ever added to that flow later).
          if (shouldFitInitialSelection) fitToBoundaries(data);
        }
      } catch (err) {
        console.error('Error loading initial selections:', err);
        setError(t('failed_load_selected', 'Failed to load selected boundaries'));
      }
    };

    loadInitialSelections();
    initialLoadDone.current = true;
  }, [initialBoundaries, t, shouldFitInitialSelection, fitToBoundaries]);

  // Enriched opening (2026-08-26 crosshair tuning, home-area-building only):
  // pre-select the wizard's already-picked home as the seed, on mount.
  // Deliberately does NOT reuse resolve_point_to_boundary against the map's
  // opening-view centre — that centre is framed on the group anchor's
  // (concelho's) own bbox (MapLocationPicker's resolveProximityView), not the
  // home freguesia's, so a point-resolve there could silently pre-select a
  // *different*, merely-nearby freguesia (Advisor-consulted, adopted). The
  // one thing we actually know for certain is the boundary id itself — fetch
  // it directly by id (the same tail `handleSetTargetLocation` uses after
  // its own resolve) and hand it to the shared seedTarget helper, so this is
  // "reuse the seed path," not "replay the point-resolve."
  // Runs once on mount only (see disable-comment) — if the wizard resumes
  // with initialBoundaries already populated (Back from Step 3), the
  // initial-selections effect above owns that state; this effect only
  // reflects it into the right control-zone stage, no re-seed RPC needed.
  useEffect(() => {
    if (!isHomeAreaBuilding) return;
    if (initialBoundaries.length > 0) {
      setHomeControlZone('grow-prompt');
      setHomeBuilderConfirmed(true);
      return;
    }
    if (!seedBoundaryId) {
      setHomeControlZone('pan-instruction');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: boundaries, error } = await supabaseFetch.rpc('get_boundaries_by_ids', {
          p_ids: [seedBoundaryId]
        });
        if (cancelled) return;
        const target = boundaries?.[0];
        if (error || !target) {
          setHomeControlZone('pan-instruction');
          return;
        }
        seedTarget(target, null, { opening: true });
        setHomeControlZone('grow-prompt');
      } catch (err) {
        if (!cancelled) {
          console.error('Enriched opening: seed-by-id failed:', err);
          setHomeControlZone('pan-instruction');
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Grow-the-edge: whenever the selection changes, refresh both the outward
  // frontier (pickable, not-yet-selected neighbours) and the internal
  // adjacency graph (which selected members touch each other — feeds the
  // contiguity guard only). One get_adjacent_freguesias call per selected
  // boundary — called with a length-1 input array each time, so only that
  // one boundary is excluded from its own result (per the RPC's
  // COALESCE/NOT-ANY contract); any OTHER currently-selected member that
  // happens to be a neighbour still comes back, which is exactly what the
  // internal-adjacency graph needs and a single combined call (which
  // excludes the WHOLE selected set from its own output) cannot supply.
  useEffect(() => {
    if (currentMode !== 'boundary' || isPickerMode || selectedBoundaries.length === 0) {
      setFrontierBoundaries([]);
      setInternalAdjacency({});
      return;
    }

    let cancelled = false;
    (async () => {
      setFrontierLoading(true);
      try {
        const selectedIds = new Set(selectedBoundaries.map(b => b.id));
        const results = await Promise.all(
          selectedBoundaries.map(b =>
            supabaseFetch.rpc('get_adjacent_freguesias', { p_boundary_ids: [b.id] })
              .then(r => ({ sourceId: b.id, data: r.data, error: r.error }))
              .catch(err => ({ sourceId: b.id, data: null, error: err }))
          )
        );
        if (cancelled) return;

        const frontierMap = new Map(); // id -> boundary object
        const adjacency = {}; // sourceId -> Set of neighbour ids also in the selection

        results.forEach(({ sourceId, data, error }) => {
          if (error || !data) {
            console.error('Adjacent-freguesias fetch failed for', sourceId, error);
            adjacency[sourceId] = new Set();
            return;
          }
          const internalNeighbours = new Set();
          data.forEach(nb => {
            if (selectedIds.has(nb.id)) {
              internalNeighbours.add(nb.id);
            } else {
              frontierMap.set(nb.id, nb);
            }
          });
          adjacency[sourceId] = internalNeighbours;
        });

        setFrontierBoundaries(Array.from(frontierMap.values()));
        setInternalAdjacency(adjacency);
      } finally {
        if (!cancelled) setFrontierLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedBoundaries, currentMode, isPickerMode]);

  const loadAvailableCountries = async () => {
    try {
      const data = await getAvailableCountries();

      setAvailableCountries(data);

      // Auto-select if only one country
      if (data && data.length === 1) {
        setSelectedCountry(data[0].country_code);
        onCountryChange?.(data[0].country_code);
      }
    } catch (err) {
      console.error('Error loading countries:', err);
      setError(tLevel('error.load_countries', 'Failed to load countries'));
    }
  };

  const getFeatureStyle = useCallback((boundary, isSelected) => {
    if (isSelected) {
      return {
        fillColor: LOCALITY_BLUE,
        fillOpacity: 0.35,
        color: LOCALITY_BLUE,
        weight: 3,
        opacity: 1.0
      };
    }
    // Frontier (pickable) boundary — visible blue interactive outline
    return {
      fillColor: 'transparent',
      fillOpacity: 0,
      color: UNSELECTED_BOUNDARY_BLUE,
      weight: 2.5,
      opacity: 0.85
    };
  }, [LOCALITY_BLUE, UNSELECTED_BOUNDARY_BLUE]);

  // seedTarget — the shared final step of every seed path (2026-08-26
  // crosshair tuning: extracted out of handleSetTargetLocation so the
  // enriched-opening effect above can reuse it without forking it, per the
  // brief's explicit instruction). Takes an already-resolved boundary
  // object — callers differ only in HOW they resolved it (live point vs a
  // known id) — and does the one thing every seed needs: for picker mode,
  // emit onSelect and stop; for multi-select, replace selectedBoundaries
  // with just this target and notify. `point` (the crosshair centre) is
  // optional — the enriched opening has no live point to offer, since it
  // never calls resolve_point_to_boundary at all (see that effect's comment
  // for why not).
  // 2026-09-01 home-picker-redefine-flow-fix-build: seedTarget is now async so the
  // autoFillFirstTierAdjacency branch can await get_adjacent_freguesias before committing the
  // selection — neither call site awaits this itself (both are fire-and-forget, matching every
  // other RPC-backed handler in this file), so a caller's own immediately-following state update
  // (e.g. handleSetTargetLocation's grow-prompt flip below) can render a beat before the fetched
  // neighbours land; the fetch is the same single round trip the existing frontier effect already
  // pays for elsewhere, just awaited here instead of fire-and-forgotten into frontier state.
  const seedTarget = useCallback(async (target, point = null, { opening = false } = {}) => {
    if (isPickerMode) {
      onSelect({
        boundaryId: target.id,
        name: target.name,
        countryCode: (target.country_code || selectedCountry)?.trim(),
        lat: point?.lat ?? null,
        lon: point?.lng ?? null,
        zoom: point?.zoom ?? null
      });
      return;
    }
    // One fetch, one fit, one fallback for both adjacency behaviours (#88 addendum 3, A1: reuse,
    // don't write a second version). autoFill selects core + first tier and fits on every seed;
    // frameFirstTierAdjacency fits on the opening seed only and selects just the core, so the map
    // then stays where the person puts it.
    const frameOnly = frameFirstTierAdjacency && !autoFillFirstTierAdjacency && opening;
    if (autoFillFirstTierAdjacency || frameOnly) {
      let neighbourhood = [target];
      try {
        const { data: adjacent, error } = await supabaseFetch.rpc('get_adjacent_freguesias', {
          p_boundary_ids: [target.id]
        });
        if (!error && adjacent?.length) neighbourhood = [target, ...adjacent];
      } catch (err) {
        console.error('Adjacency fetch failed, falling back to core only:', err);
      }
      const selected = autoFillFirstTierAdjacency ? neighbourhood : [target];
      setSelectedBoundaries(selected);
      onBoundaryChange?.(selected.map(b => b.id), selected);
      onTargetSeeded?.(target);
      // Addendum 2 (Fix 2): fit the map to core + first-tier neighbours, not a hardcoded zoom
      // delta — "3 wheel clicks" was Michael's rough gauge of the gap, not the mechanism. Every
      // boundary here (target from get_boundaries_by_ids, each neighbour from
      // get_adjacent_freguesias's own default p_include_geojson=true) already carries `.geojson`
      // — no extra fetch needed. For autoFill, addendum 3 is what keeps it landed (see
      // fitToInitialSelection below).
      fitToBoundaries(neighbourhood);
      return;
    }
    setSelectedBoundaries([target]);
    onBoundaryChange?.([target.id], [target]);
    onTargetSeeded?.(target);
  }, [isPickerMode, onSelect, onBoundaryChange, onTargetSeeded, selectedCountry, autoFillFirstTierAdjacency, frameFirstTierAdjacency, fitToBoundaries]);

  // "Set the target location" / "Set my home target" — the crosshair's
  // point-resolve (2026-08-24 redesign). Resolves the live map centre on
  // click (not on every pan/zoom — that continuous mechanism, plus its
  // leaf-only zoom gate, is retired entirely; see the task report).
  // resolve_point_to_boundary RETURNS bigint — data IS the id, no row/array
  // indexing (preserved contract, DATABASE.md / probe §C).
  const handleSetTargetLocation = useCallback(async () => {
    if (!mapRef.current || !mapMountedRef.current) return;
    setLoading(true);
    setError(null);
    try {
      // Wait out an in-flight zoom OR pan — including inertia glide after a
      // flick-drag — before reading the view (2026-09-01 crosshair
      // regression fix, probe § A3 defect i; widened to pan inertia the same
      // day). 25553b65 deliberately retired the continuous moveend/zoomend
      // mechanism, so this only defers the read for the specific case a
      // click races motion — it doesn't reinstate resolution-on-every-pan.
      // A settled click (the normal case) finds isViewAnimatingRef already
      // false and never awaits anything.
      if (isViewAnimatingRef.current) {
        await new Promise(resolve => mapRef.current.once('moveend', resolve));
        if (!mapRef.current || !mapMountedRef.current) return;
      }
      const center = mapRef.current.getCenter();
      // Zoom carried alongside the resolved point (2026-08-26 save-and-exit/parity build, Part 3
      // P3) so a caller transitioning to a fresh map (e.g. the wizard's home->área stage change)
      // can hold the zoom the user actually chose here, instead of that next stage's own opening-
      // view resolution silently reframing to its own bounding box.
      const zoom = mapRef.current.getZoom();
      const { data: targetId, error: resolveError } = await supabaseFetch.rpc('resolve_point_to_boundary', {
        p_lat: center.lat,
        p_lng: center.lng
      });
      if (resolveError || targetId == null) {
        setError(tLevel('target_resolve_failed', "Couldn't find a location there — try a different spot on the map."));
        // A failed resolve must not leave a prior selection visibly stuck
        // (2026-09-01 crosshair regression fix, probe § A3 defect ii) — same
        // reset Clear All performs (:1103-1104).
        setSelectedBoundaries([]);
        onBoundaryChange?.([], []);
        return;
      }

      const { data: boundaries, error: boundaryError } = await supabaseFetch.rpc('get_boundaries_by_ids', {
        p_ids: [targetId]
      });
      const target = boundaries?.[0];
      if (boundaryError || !target) {
        setError(tLevel('target_resolve_failed', "Couldn't find a location there — try a different spot on the map."));
        setSelectedBoundaries([]);
        onBoundaryChange?.([], []);
        return;
      }

      seedTarget(target, { ...center, zoom });
      if (isHomeAreaBuilding) {
        setHomeControlZone('grow-prompt');
        setHomeBuilderConfirmed(true);
      }
    } catch (err) {
      console.error('Set target location failed:', err);
      setError(tLevel('target_resolve_failed', "Couldn't find a location there — try a different spot on the map."));
      setSelectedBoundaries([]);
      onBoundaryChange?.([], []);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedTarget, isHomeAreaBuilding]);

  // 'dragstart' handler (2026-08-26 crosshair tuning) — the single "the user
  // has engaged with the map" signal both staged choreographies (picker-mode
  // base flow, home-area-building) key off. See MapEvents' own comment for
  // why 'dragstart' specifically, not 'movestart'/'zoomstart'.
  const handleUserPan = useCallback(() => {
    setPickerAwaitingPan(false);
    if (!isHomeAreaBuilding) return;
    setHomeControlZone(prev => {
      if (prev === 'pan-instruction') return 'button';
      if (prev === 'grow-prompt' && !homeBuilderConfirmed) return 'button';
      return prev;
    });
  }, [isHomeAreaBuilding, homeBuilderConfirmed]);

  // Select/deselect within the drawn frontier. Picker mode has no click-to-
  // pick on the map — "Set the target location" is its only pick (brief:
  // "no grow, no multi-select"). Containment/hierarchy deconfliction is
  // retired: every clickable boundary in the crosshair model is the same
  // admin level (the frontier only ever offers same-level neighbours), so
  // there is no parent/child tier to reconcile against.
  const handleBoundaryClick = useCallback((boundary) => {
    if (isPickerMode) return;
    // internalAdjacency lags one render behind selectedBoundaries (it's
    // populated by the frontier effect's own RPC round-trip) — clicking
    // while a recompute is in flight risks the contiguity guard judging a
    // deselect against a stale snapshot. Geometry never changes underfoot,
    // so staleness can only make the guard MORE conservative (never lets an
    // actual island through), but it could spuriously block a valid
    // deselect for the fetch's brief duration — simplest fix is to not
    // accept clicks until the snapshot is current again.
    if (frontierLoading) return;

    setSelectedBoundaries(prev => {
      const isSelected = prev.some(b => b.id === boundary.id);
      let newSelected;

      if (isSelected) {
        // Contiguity guard (addendum 1, ruling 2): block a deselect that
        // would strand another selected boundary. Removing the last
        // remaining boundary is always allowed — that's the reset gesture
        // that reactivates "Set the target location" (non-empty rule).
        if (prev.length > 1 && wouldStrandSelection(prev, internalAdjacency, boundary.id)) {
          setContiguityWarning(true);
          return prev;
        }
        setContiguityWarning(false);
        newSelected = prev.filter(b => b.id !== boundary.id);
      } else {
        if (effectiveMax && prev.length >= effectiveMax) {
          setCapWarning(true);
          return prev;
        }
        setCapWarning(false);
        setContiguityWarning(false);
        newSelected = [...prev, boundary];
      }

      onBoundaryChange?.(newSelected.map(b => b.id), newSelected);
      if (isHomeAreaBuilding) {
        setHomeBuilderConfirmed(true);
        setHomeControlZone(newSelected.length === 0 ? 'button' : 'grow-prompt');
      }
      return newSelected;
    });
  }, [isPickerMode, frontierLoading, effectiveMax, onBoundaryChange, internalAdjacency, isHomeAreaBuilding]);

  const handlePinClick = useCallback((e) => {
    if (currentMode !== 'pin') return;

    const { lat, lng } = e.latlng;
    setPinLocation({ lat, lng });
    onPinChange?.({ lat, lng });
  }, [currentMode, onPinChange]);

  const handleMarkerDrag = useCallback((e) => {
    const { lat, lng } = e.target.getLatLng();
    setPinLocation({ lat, lng });
    onPinChange?.({ lat, lng });
  }, [onPinChange]);

  const handleCountrySelect = (countryCode) => {
    setSelectedCountry(countryCode);
    onCountryChange?.(countryCode);
    // Don't clear selectedBoundaries — preserve selections from other countries
    setBreadcrumb([]);
    setShowCountryPicker(false);

    // Reset map view to new country's coordinates — no setMaxBounds (see the
    // 2026-08-26 map-spring-back fix note above COUNTRY_COORDS).
    if (mapRef.current) {
      const config = getMapConfig(countryCode);
      programmaticViewChangeRef.current = true;
      mapRef.current.setView(config.center, config.zoom);
      requestAnimationFrame(() => { programmaticViewChangeRef.current = false; });
      prevCountryRef.current = countryCode; // Sync ref so the effect doesn't double-fire
    }
  };

  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setSelectedCountry(newCountry);
    onCountryChange?.(newCountry);
    // Don't clear selectedBoundaries — preserve selections from other countries
    setBreadcrumb([]);
  };

  const handleModeToggle = (newMode) => {
    setCurrentMode(newMode);
    if (newMode === 'boundary') {
      setPinLocation(null);
      onPinChange?.(null);
    } else {
      setSelectedBoundaries([]);
      onBoundaryChange?.([]);
    }
  };

  // Map height - Reduced to fit within viewport without footer overlap
  const mapHeight = compact ? (isMobile ? '350px' : '500px') : (isMobile ? '350px' : '500px');

  // Control-zone instruction/prompt text (2026-08-26 crosshair tuning) — no
  // LAYOUT.md typography scale exists for this element (checked: the
  // Sizes table only covers button text/article body/mobile headline, and
  // designTokens.js's typography object is documented "used by button
  // labels — all other typography follows global CSS"), so the brief's
  // "+20%, bold, relative to its current styling" is computed here rather
  // than reached from a non-existent token: 13px (this file's existing
  // small-copy size, e.g. the selection counter) × 1.2 = 15.6, rounded to 16.
  const instructionTextStyle = {
    margin: 0,
    textAlign: 'center',
    fontSize: '16px',
    fontWeight: '700',
    color: themeColor,
    fontFamily: 'OpenSauceSans, sans-serif',
    lineHeight: '1.4'
  };

  // MapEvents is now defined outside the component body (see above)
  // to prevent React from recreating it on every parent re-render.

  return (
    <div style={{ width: '100%', fontFamily: 'OpenSauceSans, sans-serif' }}>
      {/* Mobile-specific CSS for Leaflet zoom controls - position on right side */}
      {isMobile && (
        <style>{`
          .leaflet-control-zoom {
            margin-right: 10px !important;
            margin-top: 10px !important;
          }
        `}</style>
      )}

      {/* Country dropdown (if multiple countries available). 2026-09-02 country-selection
          foundation build: revived for picker mode too (was `&& !isPickerMode`) — this was the
          entire missing affordance behind the "stuck in Portugal" family (registration, profile
          home-picker, guest quick-picker all mount in picker mode, per
          docs/briefs/2026-09-02-map-picker-country-affordance-probe-RESULTS.md §Q2). Switching
          still reuses the existing reactive setView effect (COUNTRY_COORDS-driven, unchanged) to
          reframe — no new imperative view code needed for the manual-switch case. */}
      {showCountrySelect && availableCountries.length > 1 && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
            {tLevel('label_country', 'Country')}
          </label>
          <select
            value={selectedCountry}
            onChange={handleCountryChange}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              fontSize: '14px',
              fontFamily: 'OpenSauceSans, sans-serif'
            }}
          >
            {availableCountries.map(c => (
              <option key={c.country_code} value={c.country_code}>
                {getCountryFlag(c.country_code)} {tLevel(`country_name_${c.country_code.toLowerCase()}`, c.country_name)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Instruction banner — 2026-09-02 unit-label-per-country + selector reorder build.
          Renders here (after the country-select, before the map) so MapLocationPicker's
          change/required-mode prompt reads Country first, then instruction — previously a
          MapLocationPicker-level sibling ABOVE this whole component, i.e. above the
          country-select nested inside it. Reuses the same banner colours as the div it
          replaces (#1E40AF on #EFF6FF) for visual continuity. */}
      {instructionTemplateKey && (
        <div style={{
          padding: '8px 12px',
          marginBottom: '16px',
          fontSize: '12px',
          color: '#1E40AF',
          background: '#EFF6FF',
          border: '1px solid #DBEAFE',
          borderRadius: '6px',
          fontFamily: 'OpenSauceSans, sans-serif'
        }}>
          {tLevel(instructionTemplateKey, instructionTemplateFallback, {
            unit: unitLabel || tLevel('generic_unit_fallback', 'local area')
          })}
        </div>
      )}

      {/* Breadcrumb navigation */}
      {breadcrumb.length > 0 && (
        <div style={{
          padding: '12px',
          background: '#f5f5f5',
          borderRadius: '6px',
          marginBottom: '12px',
          fontSize: '14px',
          color: '#666',
          fontFamily: 'OpenSauceSans, sans-serif'
        }}>
          {breadcrumb.map((crumb, i) => (
            <span key={i}>
              {i > 0 && <span style={{ margin: '0 8px' }}>›</span>}
              <button
                onClick={() => {
                  setBreadcrumb(breadcrumb.slice(0, i + 1));
                  // Implement drill-up logic here
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: i === breadcrumb.length - 1 ? themeColor : '#666',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontFamily: 'OpenSauceSans, sans-serif',
                  fontSize: '14px',
                  fontWeight: i === breadcrumb.length - 1 ? '600' : '400'
                }}
              >
                {isMobile && crumb.length > 10 ? crumb.slice(0, 10) + '...' : crumb}
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Country Picker Dropdown (shown when globe button clicked and multiple countries available) */}
      {showCountryPicker && availableCountries.length > 1 && (
        <div style={{
          position: 'relative',
          marginBottom: '12px',
          zIndex: 1001
        }}>
          <div style={{
            background: 'white',
            border: `2px solid ${themeColor}`,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '12px 16px',
              background: themeColor,
              color: 'white',
              fontWeight: '600',
              fontSize: '14px',
              fontFamily: 'OpenSauceSans, sans-serif',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>{tLevel('label.select_country', 'Select a Country')}</span>
              <button
                onClick={() => setShowCountryPicker(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '18px',
                  padding: '0',
                  lineHeight: '1'
                }}
              >
                ✕
              </button>
            </div>
            <div style={{
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              {availableCountries.map(country => (
                <button
                  key={country.country_code}
                  onClick={() => handleCountrySelect(country.country_code)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    background: selectedCountry === country.country_code ? `${themeColor}15` : 'white',
                    border: 'none',
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.2s',
                    fontFamily: 'OpenSauceSans, sans-serif',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedCountry !== country.country_code) {
                      e.currentTarget.style.background = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCountry !== country.country_code) {
                      e.currentTarget.style.background = 'white';
                    }
                  }}
                >
                  <div>
                    <div style={{
                      fontWeight: selectedCountry === country.country_code ? '600' : '400',
                      fontSize: '15px',
                      color: '#1a1a1a',
                      marginBottom: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ fontSize: '24px' }}>{getCountryFlag(country.country_code)}</span>
                      <span>{country.country_name}</span>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      marginLeft: '32px'
                    }}>
                      {country.boundary_count} {tLevel('label.boundaries', 'boundaries')}
                    </div>
                  </div>
                  {selectedCountry === country.country_code && (
                    <span style={{ fontSize: '18px', color: themeColor }}>✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Crosshair target control (2024-08-24 redesign — replaces the level-tab
          row entirely; staged choreography added 2026-08-26 for home-area-
          building and picker-mode surfaces — see the three branches below).
          Clear All (re-homed from the former tab-row wrapper, probe §B snag 5 /
          addendum ruling 1) is the plain multi-select branch only — unchanged
          for MyLocations/AddEditGroup, which this tuning build does not touch. */}
      {currentMode === 'boundary' && isHomeAreaBuilding && (
        // Home-area-building: exactly one of three things, per the brief's own
        // framing — no Clear All in this zone (the wizard's own Back button and
        // the deselect-to-zero rule already cover "start over"). copyVariant
        // (2026-08-26 save-and-exit/parity build) substitutes MyLocations'
        // "new location"/"new area" wording for the home surfaces' "home"
        // wording in all three strings — same choreography, different copy.
        // Save-and-exit (Part 2) sits alongside the grow-prompt once there's a
        // built selection to exit with, for callers that pass onSaveAndExit.
        // The account-setup área stage (RegisterLocationStep) no longer does:
        // since #88 addendum 3 (A4) it renders its one finish action itself,
        // below the map.
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '12px',
          minHeight: '50px'
        }}>
          {homeControlZone === 'pan-instruction' && (
            <p style={instructionTextStyle}>
              {copyVariant === 'newLocation'
                ? tLevel('new_location_pan_instruction', 'Move your new location under the central target by scrolling the map')
                : tLevel('home_pan_instruction', 'Move your home location under the central target by scrolling the map')}
            </p>
          )}
          {homeControlZone === 'button' && (
            <StandardButton
              variant="action"
              label={copyVariant === 'newLocation'
                ? tLevel('set_new_location_target', 'Set your new location')
                : tLevel('set_home_target', 'Set my home target')}
              onClick={handleSetTargetLocation}
              disabled={loading}
            />
          )}
          {homeControlZone === 'grow-prompt' && (
            <p style={instructionTextStyle}>
              {copyVariant === 'newLocation'
                ? tLevel('new_area_grow_prompt', 'Now choose any adjoining areas you want to be a part of your new area')
                : (growPromptLabel || tLevel('home_grow_prompt', 'Now choose any adjoining areas you want to be a part of your home area'))}
            </p>
          )}
          {onSaveAndExit && selectedBoundaries.length > 0 && (
            <StandardButton
              variant="action"
              label={saveAndExitLabel || tLevel('save_and_exit', 'Save and exit')}
              onClick={onSaveAndExit}
              disabled={loading}
            />
          )}
        </div>
      )}

      {currentMode === 'boundary' && !isHomeAreaBuilding && isPickerMode && (
        // Plain picker-mode surfaces (guest Change Location, MyProfile
        // change-home, registration gate, the wizard's own home stage) —
        // base pan-instruction/button staging, generic (no "home") wording,
        // no grow phase (there's nothing to grow: onSelect ends the flow).
        // pickerConfirmLabel (Part 3 P2) lets one surface (registration's
        // home stage) override the button label without renaming it for
        // every other isPickerMode caller.
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '12px',
          minHeight: '50px'
        }}>
          {pickerAwaitingPan ? (
            <p style={instructionTextStyle}>
              {tLevel('pan_instruction', 'Move the target location under the crosshair by scrolling the map')}
            </p>
          ) : (
            <StandardButton
              variant="action"
              label={pickerConfirmLabel || tLevel('set_target_location', 'Set the target location')}
              onClick={handleSetTargetLocation}
              disabled={loading}
            />
          )}
        </div>
      )}

      {currentMode === 'boundary' && !isHomeAreaBuilding && !isPickerMode && (
        // Plain multi-select (MyLocations, AddEditGroup) — unchanged from the
        // parent crosshair build: binary button-or-Clear-All, no staging.
        // Save-and-exit (Part 2) joins Clear All once there's a built
        // selection — the only "finish" action these two surfaces get, since
        // neither has a pre-existing outer button of its own.
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '12px',
          justifyContent: selectedBoundaries.length === 0 ? 'center' : 'flex-end',
          alignItems: 'center'
        }}>
          {selectedBoundaries.length === 0 && (
            <StandardButton
              variant="action"
              label={tLevel('set_target_location', 'Set the target location')}
              onClick={handleSetTargetLocation}
              disabled={loading}
            />
          )}

          {selectedBoundaries.length > 0 && onSaveAndExit && (
            <StandardButton
              variant="action"
              label={saveAndExitLabel || tLevel('save_and_exit', 'Save and exit')}
              onClick={onSaveAndExit}
              disabled={loading}
            />
          )}

          {selectedBoundaries.length > 0 && (
            <StandardButton
              variant="destructive"
              label={tLevel('clear_all', 'Clear All')}
              wrapLabel
              width={88}
              heightOverride={50}
              confirmMessage={tLevel('clear_all_confirm', "This clears every location you've selected. This cannot be undone.")}
              onClick={() => {
                setSelectedBoundaries([]);
                onBoundaryChange?.([], []);
              }}
            />
          )}
        </div>
      )}

      {/* Selection counter (user mode only, hidden in picker mode) */}
      {effectiveMax && !isPickerMode && (
        <div style={{
          padding: '6px 12px',
          background: selectedBoundaries.length >= effectiveMax ? '#FEF3C7' : '#F0F9FF',
          border: `1px solid ${selectedBoundaries.length >= effectiveMax ? '#F59E0B' : '#BAE6FD'}`,
          borderRadius: '6px',
          marginBottom: '8px',
          fontSize: '13px',
          color: selectedBoundaries.length >= effectiveMax ? '#92400E' : '#0C4A6E',
          fontFamily: 'OpenSauceSans, sans-serif',
          fontWeight: '500',
          textAlign: 'center'
        }}>
          {t('location.saved_areas.counter', { count: String(selectedBoundaries.length), max: String(effectiveMax) })}
        </div>
      )}

      {/* Cap warning message (hidden in picker mode) */}
      {capWarning && effectiveMax && !isPickerMode && (
        <div style={{
          padding: '10px 14px',
          background: '#FEF3C7',
          border: '1px solid #F59E0B',
          borderRadius: '6px',
          marginBottom: '12px',
          fontSize: '13px',
          color: '#92400E',
          fontFamily: 'OpenSauceSans, sans-serif',
          fontWeight: '500'
        }}>
          {t('location.saved_areas.cap_reached')}
        </div>
      )}

      {/* Contiguity warning (addendum 1, ruling 2) — a blocked deselect fails
          gracefully with this inline cue rather than silently no-op'ing. */}
      {contiguityWarning && !isPickerMode && (
        <div style={{
          padding: '10px 14px',
          background: '#FEF3C7',
          border: '1px solid #F59E0B',
          borderRadius: '6px',
          marginBottom: '12px',
          fontSize: '13px',
          color: '#92400E',
          fontFamily: 'OpenSauceSans, sans-serif',
          fontWeight: '500'
        }}>
          {tLevel('contiguity_blocked', "You can't remove this — it would split your área into separate pieces. Try removing from an edge instead.")}
        </div>
      )}

      {/* Error and loading (2026-09-15 build-your-area stall fix, brief item 3 — relocated from
          the very bottom of this component, below the map, where RegisterLocationStep's fixed
          620px/460px overflow:hidden frame clipped them out of sight entirely). They now sit in
          this same message zone as the cap and contiguity warnings above, so every message this
          component can raise appears in one place, above the map, on all five mounts
          (MapLocationPicker x2, MyLocations x2, AddEditGroup). Shape follows the sibling warnings
          — same padding, radius, font and weight — recoloured red rather than amber. Colours stay
          raw literals: LAYOUT.md § Design Tokens names `locality` among the domains the token
          system has not reached, and says not to mass-rewrite across domains in one pass. */}
      {error && (
        <div style={{
          padding: '10px 14px',
          background: '#FEE2E2',
          border: '1px solid #DC2626',
          borderRadius: '6px',
          marginBottom: '12px',
          fontSize: '13px',
          color: '#991B1B',
          fontFamily: 'OpenSauceSans, sans-serif',
          fontWeight: '500'
        }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{
          padding: '10px 14px',
          marginBottom: '12px',
          fontSize: '13px',
          color: '#666',
          fontFamily: 'OpenSauceSans, sans-serif',
          textAlign: 'center'
        }}>
          {tLevel('status.loading', 'Loading...')}
        </div>
      )}

      {/* Map container */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: mapHeight,
        marginBottom: '16px'
      }}>

        {/* Map */}
        <MapContainer
          center={getMapConfig(selectedCountry).center}
          zoom={getMapConfig(selectedCountry).zoom}
          inertia={!disableInertia}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '8px',
            border: `2px solid ${themeColor}`
          }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution={`&copy; OpenStreetMap contributors${selectedCountry === 'PT' ? ` | ${tLevel('caop_attribution', 'Boundaries: © DGT (CAOP2025) CC BY 4.0')}` : ''}`}
          />
          <MapEvents
            initialBounds={initialBounds}
            initialCenter={initialCenter}
            initialZoom={initialZoom}
            currentMode={currentMode}
            handlePinClick={handlePinClick}
            mapRef={mapRef}
            mapMountedRef={mapMountedRef}
            onUserPan={currentMode === 'boundary' && (isHomeAreaBuilding || isPickerMode) ? handleUserPan : null}
            programmaticViewChangeRef={programmaticViewChangeRef}
            isViewAnimatingRef={isViewAnimatingRef}
          />

          {/* Render all selected boundaries (the target plus any grown área) */}
          {currentMode === 'boundary' && selectedBoundaries.map(boundary => (
            <GeoJSON
              key={`boundary-${boundary.id}-selected`}
              data={boundary.geojson}
              style={getFeatureStyle(boundary, true)}
              eventHandlers={{
                click: () => handleBoundaryClick(boundary)
              }}
            />
          ))}

          {/* Render the grow-the-edge frontier — freguesias touching the
              current selection, not yet selected. The only boundaries ever
              clickable-to-add (click-gating, probe §E): there is no more
              "any boundary on screen" tier. */}
          {currentMode === 'boundary' && !isPickerMode && frontierBoundaries.map(boundary => {
            const atCap = effectiveMax && selectedBoundaries.length >= effectiveMax;
            return (
              <GeoJSON
                key={`boundary-${boundary.id}-frontier`}
                data={boundary.geojson}
                style={getFeatureStyle(boundary, false)}
                eventHandlers={{
                  click: () => handleBoundaryClick(boundary),
                  mouseover: (e) => {
                    if (!atCap) {
                      e.target.setStyle({
                        fillColor: LOCALITY_BLUE,
                        fillOpacity: 0.1
                      });
                    }
                  },
                  mouseout: (e) => {
                    if (!atCap) {
                      e.target.setStyle({
                        fillColor: 'transparent',
                        fillOpacity: 0
                      });
                    }
                  }
                }}
              />
            );
          })}

          {/* Render pin marker */}
          {currentMode === 'pin' && pinLocation && (
            <Marker
              position={[pinLocation.lat, pinLocation.lng]}
              draggable={true}
              eventHandlers={{
                dragend: handleMarkerDrag
              }}
              ref={markerRef}
            />
          )}
        </MapContainer>

        {/* Crosshair reticle — fixed at map centre while aiming (no target
            set, or the home-builder's button has reappeared after a pan on
            an unconfirmed auto-seed — see homeControlZone). Non-interactive;
            the user pans/zooms the actual map underneath it, then clicks the
            button above. */}
        {currentMode === 'boundary' && (isHomeAreaBuilding ? homeControlZone !== 'grow-prompt' : selectedBoundaries.length === 0) && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '32px',
              height: '32px',
              pointerEvents: 'none',
              zIndex: tokens.zIndex.overlay
            }}
          >
            <svg width="32" height="32" viewBox="0 0 32 32">
              <circle cx="16" cy="16" r="9" fill="none" stroke={LOCALITY_BLUE} strokeWidth="2.5" opacity="0.9" />
              <line x1="16" y1="0" x2="16" y2="8" stroke={LOCALITY_BLUE} strokeWidth="2.5" opacity="0.9" />
              <line x1="16" y1="24" x2="16" y2="32" stroke={LOCALITY_BLUE} strokeWidth="2.5" opacity="0.9" />
              <line x1="0" y1="16" x2="8" y2="16" stroke={LOCALITY_BLUE} strokeWidth="2.5" opacity="0.9" />
              <line x1="24" y1="16" x2="32" y2="16" stroke={LOCALITY_BLUE} strokeWidth="2.5" opacity="0.9" />
            </svg>
          </div>
        )}

        {/* "Loading parishes…" — the frontier fetch's own in-flight state.
            Reuses the same overlay slot/style and translation key as the
            former viewport-loading hint. */}
        {frontierLoading && (
          <div style={{
            position: 'absolute',
            bottom: '12px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '8px 16px',
            background: '#F0F9FF',
            border: '1px solid #BAE6FD',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#0C4A6E',
            fontFamily: 'OpenSauceSans, sans-serif',
            fontWeight: '500',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            zIndex: 1000,
            pointerEvents: 'none'
          }}>
            {tLevel('loading_freguesias', 'Loading parishes…')}
          </div>
        )}
      </div>

      {/* Mode toggle (if both boundary and pin allowed) */}
      {allowBoundary && allowPin && (
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '16px'
        }}>
          <button
            onClick={() => handleModeToggle('boundary')}
            style={{
              flex: 1,
              padding: '12px',
              background: currentMode === 'boundary' ? themeColor : '#f5f5f5',
              color: currentMode === 'boundary' ? 'white' : '#333',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontFamily: 'OpenSauceSans, sans-serif',
              fontWeight: '600',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            {tLevel('mode.select_area', 'Select Area')}
          </button>
          <button
            onClick={() => handleModeToggle('pin')}
            style={{
              flex: 1,
              padding: '12px',
              background: currentMode === 'pin' ? themeColor : '#f5f5f5',
              color: currentMode === 'pin' ? 'white' : '#333',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontFamily: 'OpenSauceSans, sans-serif',
              fontWeight: '600',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            {tLevel('mode.drop_pin', 'Drop Pin')}
          </button>
        </div>
      )}

      {/* Pin location display */}
      {currentMode === 'pin' && pinLocation && (
        <div style={{
          padding: '8px 12px',
          background: '#f5f5f5',
          borderRadius: '6px',
          fontSize: '13px',
          color: '#666',
          marginBottom: '16px',
          fontFamily: 'OpenSauceSans, sans-serif'
        }}>
          <strong>{tLevel('label.location', 'Location')}:</strong>{' '}
          {pinLocation.lat.toFixed(4)}, {pinLocation.lng.toFixed(4)}
        </div>
      )}

      {/* Loading/Error state */}
      {/* The error and loading blocks that stood here were moved ABOVE the map (see the message
          zone by the cap/contiguity warnings) — 2026-09-15 build-your-area stall fix, brief item
          3. Below the map they were clipped out of existence by RegisterLocationStep's frame. */}
    </div>
  );
};

export default UnifiedLocationSelector;
